import {
  Button,
  HStack,
  Icon,
  Text,
  ModalCloseButton,
  Spacer,
  useDisclosure
} from '@chakra-ui/react';
import { format } from 'date-fns';
import { map } from 'lodash-es';

import { SFEnvelopeIcon, SFPaperPlaneIcon } from '@assets/icons';
import { useStyledToast } from '@util/hooks';
import { HIDE_SCROLLBAR } from '@styles/css-configs';

import StyledModal from '@components/general/modals/styled-modal.component';
import ShareSummary from '@components/share-summary/share-summary.component';
import * as Sentry from '@sentry/react';
import { useShareInsight } from '@services/query-services/insights.query-service';
import InsightPDF from '@components/pdf/insight-pdf.component';
import { useUserStore } from '@store/app.store';
import { useInitiativeById } from '@services/query-services/initiatives.query-service';

export default function ShareInsightModal({ insight, fullCards }) {
  const shareInsightDisclosure = useDisclosure();
  const user = useUserStore((state) => state.user);
  const tenant = useUserStore((state) => state.tenant);
  const toast = useStyledToast();
  const { mutateAsync: shareInsight, isSharing } = useShareInsight();
  const { data: initiative } = useInitiativeById(insight?.initiative?.id, {
    enabled: !!insight?.initiative?.id
  });

  const handleShareSubmit = async (values) => {
    await shareInsight(
      {
        id: insight?.id,
        body: {
          emails: map(values?.recipients, 'email'),
          message: values?.message,
          guest: values?.guest
        }
      },
      {
        onSuccess: () => {
          toast({
            title: 'Successfully shared insight',
            status: 'success',
            isClosable: true
          });
          shareInsightDisclosure.onClose();
        },
        onError: (error) => {
          const title = insight?.id
            ? 'Something went wrong while sharing insight. Please try again.'
            : 'Failed to generate shareable summary. If this continues please reach out to FORA support.';
          Sentry.withScope((scope) => {
            scope.setTag('component', 'ShareInsightModal');
            scope.setContext('insight', { insightId: insight?.id });
            scope.setExtra('error_message', 'Share Submit Error');
            Sentry.captureException(error);
          });
          toast({
            title,
            status: 'error',
            isClosable: true
          });
        }
      }
    );
  };

  return (
    <StyledModal
      title='Send to...'
      openButton={
        <Button
          size='md'
          variant='reskinWhite'
          rounded='lg'
          pl={2.5}
          fontWeight={450}
          leftIcon={
            <SFPaperPlaneIcon
              boxSize={6}
              color='brandPrimary.primary'
            />
          }
        >
          Share
        </Button>
      }
      disclosure={shareInsightDisclosure}
      modalProps={{ scrollBehavior: 'inside', blockScrollOnMount: false }}
      modalContentProps={{ maxH: 'calc(100vh - 48px)', w: { base: '550px' } }}
      modalBodyProps={{
        overflowY: 'scroll',
        sx: HIDE_SCROLLBAR
      }}
      customHeader={
        <HStack
          color='brandPrimary.onPrimary'
          bgColor='brandPrimary.primary'
          py={4}
          px={{ base: 6, sm: 4 }}
        >
          <Icon
            as={SFEnvelopeIcon}
            boxSize={5}
          />
          <Text fontWeight={500}>Share Insight</Text>
          <Spacer />
          <ModalCloseButton />
        </HStack>
      }
    >
      <ShareSummary
        isSensitive={false}
        summaryType='Insight'
        isSubmitting={isSharing}
        onSubmit={handleShareSubmit}
        disclosure={shareInsightDisclosure}
        pdfTitle={`${insight?.initiative?.title} - ${format(insight?.updated_at, 'PP')}`}
        pdfDocument={
          <InsightPDF
            insight={insight}
            initiative={initiative}
            fullCards={fullCards}
            userName={user?.name}
            tenantName={tenant?.name || insight?.tenant?.name}
          />
        }
      />
    </StyledModal>
  );
}
